/**
 * Shared UI atoms for Live Classes user views (LiveClassesUserPage +
 * LiveClassArchiveListPage).
 *
 * Both surfaces list published archives and filter by function/orals/written,
 * so extracting these components here removes the duplication and keeps
 * design updates in one place.
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Archive, PlayCircle, Calendar, User as UserIcon, MessagesSquare, ArrowRight,
} from 'lucide-react';

export const fmtDateOnly = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', dateStyle: 'medium',
    });
  } catch {
    return iso;
  }
};

// -------------------- ArchiveCard --------------------
export const ArchiveCard = ({ cls, navigate, fnById, from = '/live-classes' }) => {
  const fnChips = (cls.functions || []).map((id) => fnById(id)).filter(Boolean);
  return (
    <Card
      className="h-full transition-shadow hover:shadow-md cursor-pointer group"
      onClick={() => navigate(`/live-classes/${cls.id}/archive`, { state: { from } })}
      data-testid={`archive-card-${cls.id}`}
    >
      <CardContent className="p-5 h-full">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className="bg-emerald-100 text-emerald-800 border-0 gap-1">
                <Archive className="w-3 h-3" /> Archived
              </Badge>
              {cls.has_recording && (
                <Badge variant="outline" className="text-xs gap-1">
                  <PlayCircle className="w-3 h-3" /> Recording
                </Badge>
              )}
              {Array.isArray(cls.grades) && cls.grades.map((g) => (
                <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
              ))}
              {Array.isArray(cls.mmd_locations) && cls.mmd_locations.map((m) => (
                <Badge key={m} variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-300">
                  {m}
                </Badge>
              ))}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-[#0A7EA4] transition-colors">
              {cls.title}
            </h3>
            {cls.summary_preview && (
              <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                {cls.summary_preview}{cls.summary_preview.length >= 180 ? '…' : ''}
              </p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{fmtDateOnly(cls.start_time)}</span>
              <span className="flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5" />{cls.host_user_name}</span>
              {cls.qna_count > 0 && (
                <span className="flex items-center gap-1.5 text-[#0A7EA4]">
                  <MessagesSquare className="w-3.5 h-3.5" />
                  {cls.qna_count} {cls.qna_count === 1 ? 'question' : 'questions'}
                </span>
              )}
            </div>
            {fnChips.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {fnChips.map((fn) => (
                  <span
                    key={fn.id}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${fn.exam_type === 'orals'
                      ? 'bg-[#0A7EA4]/8 text-[#0A7EA4] border-[#0A7EA4]/25'
                      : 'bg-purple-50 text-purple-700 border-purple-200'}`}
                    title={`${fn.name} · ${(fn.exam_type || 'general').toUpperCase()}`}
                  >
                    <span className="opacity-60 mr-0.5">F{fn.function_number}</span>
                    {fn.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center self-center">
            <div className="w-10 h-10 rounded-full bg-[#0A7EA4]/10 group-hover:bg-[#0A7EA4]/20 flex items-center justify-center transition-colors">
              <ArrowRight className="w-4 h-4 text-[#0A7EA4]" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// -------------------- FunctionFilterBlock --------------------
export const FunctionFilterBlock = ({ groupedFunctions, selected, onToggle, onClear, fnById, sectionKey }) => {
  const totalCatalog =
    groupedFunctions.orals.length + groupedFunctions.written.length + groupedFunctions.other.length;
  if (totalCatalog === 0) return null;

  const selectedNames = selected.map((id) => fnById(id)?.name).filter(Boolean);

  return (
    <div className="mb-4 p-3 rounded-lg border bg-gray-50/60" data-testid={`${sectionKey}-function-filter`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Filter by function</p>
          <div className="flex items-center gap-2 text-[10px] text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-[#0A7EA4] border border-[#0A7EA4]" />
              Orals
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-600 border border-purple-600" />
              Written
            </span>
          </div>
        </div>
        {selected.length > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-[#0A7EA4] hover:underline"
            data-testid={`${sectionKey}-function-filter-clear`}
          >
            Clear ({selected.length})
          </button>
        )}
      </div>

      {[
        { key: 'orals', label: 'Orals', items: groupedFunctions.orals },
        { key: 'written', label: 'Written', items: groupedFunctions.written },
        { key: 'other', label: 'Other', items: groupedFunctions.other },
      ].filter((g) => g.items.length > 0).map((group) => (
        <div key={group.key} className="mb-2 last:mb-0" data-testid={`${sectionKey}-function-group-${group.key}`}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            {group.label}
            <span className="ml-1 text-gray-400 font-normal">({group.items.length})</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {group.items.map((fn) => {
              const active = selected.includes(fn.id);
              const activeCls = group.key === 'orals'
                ? 'bg-[#0A7EA4] text-white border-[#0A7EA4]'
                : group.key === 'written'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-gray-600 text-white border-gray-600';
              const hoverCls = group.key === 'orals'
                ? 'hover:border-[#0A7EA4] hover:text-[#0A7EA4]'
                : group.key === 'written'
                  ? 'hover:border-purple-500 hover:text-purple-700'
                  : 'hover:border-gray-500 hover:text-gray-800';
              return (
                <button
                  key={fn.id}
                  onClick={() => onToggle(fn.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${active
                    ? activeCls
                    : `bg-white text-gray-700 border-gray-300 ${hoverCls}`}`}
                  data-testid={`${sectionKey}-function-chip-${fn.id}`}
                  title={fn.name}
                >
                  <span className="opacity-70 mr-1">F{fn.function_number}</span>
                  {fn.name}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {selected.length > 0 && (
        <p className="text-[11px] text-gray-600 mt-2 pt-2 border-t border-gray-200" data-testid={`${sectionKey}-function-filter-summary`}>
          Showing classes tagged: <span className="font-semibold text-gray-800">{selectedNames.join(', ')}</span>
        </p>
      )}
    </div>
  );
};
